import { z } from 'zod'

import {
  ActionPlan,
  describeActionPreconditions,
  describeExpectedPostconditions,
  type ActionPlan as ActionPlanValue,
  type ActionPlanLifecycleStep,
  type ActionPlanStep,
  type CompiledAction,
  type CompiledAssertion,
  type CompiledLocator,
  locatorPriority
} from './plan.ts'
import {
  ScenarioAction,
  executableScenarioHash,
  type LocatorHint,
  type ScenarioAction as ScenarioActionValue,
  type ScenarioAssertion,
  type ScenarioLifecycleStep,
  type WebTestScenario
} from './scenario.ts'
import { sha256CanonicalJson } from './serialization.ts'

export const EXPLORATION_INCOMPLETE_PREFIX = '[exploration-incomplete]'

export class ExplorationIncompleteError extends Error {
  constructor(message: string, options?: ErrorOptions) {
    super(`${EXPLORATION_INCOMPLETE_PREFIX} ${message}`, options)
    this.name = 'ExplorationIncompleteError'
  }
}

export function isExplorationIncompleteError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)
  return (
    error instanceof ExplorationIncompleteError ||
    (error instanceof Error && error.name === 'ExplorationIncompleteError') ||
    message.includes(EXPLORATION_INCOMPLETE_PREFIX)
  )
}

export const ExplorationBudget = z
  .object({
    maxTurns: z.number().int().min(1).max(50),
    wallTimeMs: z
      .number()
      .int()
      .min(100)
      .max(15 * 60_000),
    maxScreenshotBytes: z.number().int().min(0).max(5_000_000),
    maxDiscoveryAttempts: z.number().int().min(1).max(3)
  })
  .strict()
export type ExplorationBudget = z.infer<typeof ExplorationBudget>

export const DEFAULT_EXPLORATION_BUDGET: ExplorationBudget = Object.freeze({
  maxTurns: 18,
  wallTimeMs: 10 * 60_000,
  maxScreenshotBytes: 700_000,
  maxDiscoveryAttempts: 2
})

export function resolveExplorationBudget(
  overrides?: Partial<ExplorationBudget>
): ExplorationBudget {
  const definedOverrides = Object.fromEntries(
    Object.entries(overrides ?? {}).filter(([, value]) => value !== undefined)
  )
  return ExplorationBudget.parse({
    ...DEFAULT_EXPLORATION_BUDGET,
    ...definedOverrides
  })
}

export const ExplorationDecision = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('act'),
      scenarioStepId: z.string().min(1).max(160),
      action: ScenarioAction,
      reason: z.string().min(1).max(1_000)
    })
    .strict(),
  z
    .object({
      kind: z.literal('complete'),
      reason: z.string().min(1).max(1_000)
    })
    .strict(),
  z
    .object({
      kind: z.literal('blocked'),
      reason: z.string().min(1).max(2_000)
    })
    .strict()
])
export type ExplorationDecision = z.infer<typeof ExplorationDecision>

export const EXPLORATION_DECISION_CORRECTION_INSTRUCTION =
  'Return only one JSON object in exactly one of these shapes: {"kind":"act","scenarioStepId":"<declared step id>","action":<one complete allowed scenario action>,"reason":"<non-empty explanation>"}, {"kind":"complete","reason":"<non-empty explanation>"}, or {"kind":"blocked","reason":"<non-empty explanation>"}. Include every property shown for the selected kind and no additional properties.'

export interface RecordedExplorationAction {
  scenarioStepId: string
  action: ScenarioActionValue
  reason: string
  locatorCandidates?: {
    target?: LocatorHint[]
    source?: LocatorHint[]
  }
}

export function validateExplorationDecision(
  decision: ExplorationDecision,
  scenario: WebTestScenario,
  recorded: RecordedExplorationAction[]
): void {
  if (decision.kind !== 'act') return
  const step = scenario.steps.find((candidate) => candidate.id === decision.scenarioStepId)
  if (!step) throw new Error(`Model referenced unknown scenario step "${decision.scenarioStepId}"`)
  if (!step.action) throw new Error(`Scenario step "${step.id}" does not permit an action`)
  if (recorded.some((action) => action.scenarioStepId === step.id)) {
    throw new Error(`Model repeated scenario step "${step.id}"`)
  }
  if (decision.action.kind !== step.action.kind) {
    throw new Error(
      `Model changed action kind for "${step.id}" from ${step.action.kind} to ${decision.action.kind}`
    )
  }
  if (decision.action.kind === 'fill' || decision.action.kind === 'select') {
    const authored = step.action
    if (
      authored.kind !== decision.action.kind ||
      JSON.stringify(decision.action.value) !== JSON.stringify(authored.value)
    ) {
      throw new Error(`Model changed authored input value for "${step.id}"`)
    }
  }
}

export function orderLocatorCandidates(
  hint: LocatorHint,
  candidates: readonly LocatorHint[] = []
): LocatorHint[] {
  const locatorIdentity = (candidate: LocatorHint): string => {
    switch (candidate.by) {
      case 'role':
        return JSON.stringify({
          by: candidate.by,
          role: candidate.role,
          name: candidate.name,
          exact: candidate.exact
        })
      case 'label':
      case 'placeholder':
      case 'text':
        return JSON.stringify({
          by: candidate.by,
          value: candidate.value,
          exact: candidate.exact
        })
      case 'test-id':
      case 'css':
        return JSON.stringify({ by: candidate.by, value: candidate.value })
    }
  }
  const authoredIdentity = locatorIdentity(hint)
  const unique: LocatorHint[] = []
  for (const candidate of [...candidates, hint]) {
    const identity = locatorIdentity(candidate)
    const existingIndex = unique.findIndex((value) => locatorIdentity(value) === identity)
    if (existingIndex === -1) unique.push(candidate)
    else if (candidate === hint) unique[existingIndex] = hint
  }
  unique.sort((left, right) => locatorPriority(left) - locatorPriority(right))
  if (unique.length <= 5) return unique

  const bounded = unique.slice(0, 5)
  if (!bounded.some((candidate) => locatorIdentity(candidate) === authoredIdentity)) {
    bounded[bounded.length - 1] = hint
    bounded.sort((left, right) => locatorPriority(left) - locatorPriority(right))
  }
  return bounded
}

function compiledLocator(
  hint: LocatorHint,
  reason: string,
  candidates: LocatorHint[] = []
): CompiledLocator {
  const unique = orderLocatorCandidates(hint, candidates)
  return {
    primary: unique[0]!,
    fallbacks: unique.slice(1, 5),
    rationale: reason.slice(0, 1_000)
  }
}

function compileAction(
  action: ScenarioActionValue,
  reason: string,
  candidates: RecordedExplorationAction['locatorCandidates'] = {}
): CompiledAction {
  const target = (hint: LocatorHint): CompiledLocator =>
    compiledLocator(hint, reason, candidates.target)
  const source = (hint: LocatorHint): CompiledLocator =>
    compiledLocator(hint, reason, candidates.source)
  switch (action.kind) {
    case 'navigate':
      return action
    case 'click':
      return {
        kind: 'click',
        target: target(action.target),
        ...(action.button === undefined ? {} : { button: action.button })
      }
    case 'fill':
      return {
        kind: 'fill',
        target: target(action.target),
        value: action.value,
        sensitive: action.sensitive ?? false
      }
    case 'select':
      return {
        kind: 'select',
        target: target(action.target),
        value: action.value,
        sensitive: action.sensitive ?? false
      }
    case 'check':
    case 'uncheck':
    case 'hover':
      return { kind: action.kind, target: target(action.target) }
    case 'press':
      return {
        kind: 'press',
        ...(action.target ? { target: target(action.target) } : {}),
        key: action.key,
        modifiers: action.modifiers ?? []
      }
    case 'drag':
      return { kind: 'drag', source: source(action.source), target: target(action.target) }
    case 'wait':
      switch (action.for.kind) {
        case 'visible':
          return { kind: 'wait-visible', target: target(action.for.target) }
        case 'hidden':
          return { kind: 'wait-hidden', target: target(action.for.target) }
        case 'route':
          return { kind: 'wait-route', route: action.for.route }
        case 'text':
          return {
            kind: 'wait-text',
            target: target(action.for.target),
            value: action.for.value
          }
        case 'network-idle':
          return { kind: 'wait-network-idle' }
      }
    case 'upload':
      return { kind: 'upload', target: target(action.target), fixture: action.fixture }
  }
}

function compileAssertion(assertion: ScenarioAssertion): CompiledAssertion {
  const target = 'target' in assertion ? assertion.target : undefined
  return {
    source: assertion,
    ...(target
      ? {
          target: compiledLocator(
            target,
            'Preserves the scenario-authored assertion locator without weakening it.'
          )
        }
      : {})
  }
}

function compileLifecycleStep(
  step: ScenarioLifecycleStep,
  phase: 'setup' | 'cleanup',
  index: number
): ActionPlanLifecycleStep {
  const reason = `Preserves the scenario-authored ${phase} action as a bounded deterministic lifecycle step.`
  const action = compileAction(step.action, reason)
  return {
    id: `${phase}:${String(index + 1).padStart(3, '0')}:${step.id}`,
    scenarioStepId: step.id,
    description: step.description,
    source: step.action,
    action,
    preconditions: describeActionPreconditions(action),
    expectedPostconditions: describeExpectedPostconditions(action, []),
    timeoutMs: step.timeoutMs ?? 15_000
  }
}

export async function compileExplorationTrace(
  scenario: WebTestScenario,
  recorded: RecordedExplorationAction[],
  options: {
    target: 'web-lab' | 'electron'
    compilerVersion: string
    createdAt?: string
  }
): Promise<ActionPlanValue> {
  const scenarioHash = await executableScenarioHash(scenario)
  const plan = ActionPlan.parse({
    schemaVersion: 1,
    scenarioId: scenario.id,
    scenarioHash,
    compilerVersion: options.compilerVersion,
    executorProtocolVersion: 1,
    createdAt: options.createdAt ?? new Date().toISOString(),
    createdBy: 'ai-exploration',
    target: options.target,
    assumptions: {
      viewport: scenario.environment.viewport,
      locale: scenario.environment.locale,
      timezone: scenario.environment.timezone
    },
    ...(scenario.setup === undefined
      ? {}
      : {
          setup: scenario.setup.map((step, index) => compileLifecycleStep(step, 'setup', index))
        }),
    steps: scenario.steps.map((step, index) => {
      const explored = recorded.find((action) => action.scenarioStepId === step.id)
      const action = explored
        ? compileAction(explored.action, explored.reason, explored.locatorCandidates)
        : step.action
          ? compileAction(
              step.action,
              'Uses the scenario-authored action because no exploratory replacement was required.'
            )
          : undefined
      const assertions = step.assertions.map(compileAssertion)
      return {
        id: `plan:${String(index + 1).padStart(3, '0')}:${step.id}`,
        scenarioStepId: step.id,
        description: step.description,
        ...(action === undefined
          ? {}
          : {
              action,
              preconditions: describeActionPreconditions(action),
              expectedPostconditions: describeExpectedPostconditions(action, assertions)
            }),
        assertions,
        timeoutMs: step.timeoutMs ?? 15_000
      }
    }),
    ...(scenario.cleanup === undefined
      ? {}
      : {
          cleanup: scenario.cleanup.map((step, index) =>
            compileLifecycleStep(step, 'cleanup', index)
          )
        })
  })
  return ActionPlan.parse({ ...plan, planHash: await sha256CanonicalJson(plan) })
}
